"use client";

import { ChevronRight, type LucideIcon } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";

export function NavMain({
  items,
  groupLabel = "Platform",
}: {
  items: {
    title: string;
    url: string;
    icon: LucideIcon;
    isActive?: boolean;
    items?: {
      title: string;
      url: string;
    }[];
  }[];
  groupLabel?: string;
}) {
  const { pathname } = useLocation();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="mb-2 text-xs font-semibold text-sidebar-foreground/90">
        {groupLabel}
      </SidebarGroupLabel>
      <SidebarMenu className="space-y-0">
        {items.map((item) => {
          const isRootUrl = item.url === "/";
          const isParentActive =
            pathname === item.url ||
            (!isRootUrl && pathname.startsWith(`${item.url}/`)) ||
            (item.items?.some(
              (sub) =>
                pathname === sub.url || pathname.startsWith(`${sub.url}/`)
            ) ??
              false);

          const activeBarClasses = isParentActive
            ? "before:content-[''] before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[3px] before:rounded-r before:bg-confluence group-data-[collapsible=icon]:before:hidden"
            : "";

          const navButtonClasses = `relative h-9 px-2.5 py-2 text-sm text-sidebar-foreground transition-colors group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!px-0 group-data-[collapsible=icon]:!py-0 ${activeBarClasses}`;

          // Inline color beats parent menu-button text utilities (esp. active Confluence).
          const iconColor = isCollapsed
            ? isParentActive
              ? "#FFFFFF"
              : "hsl(var(--shallows))"
            : "hsl(var(--confluence))";

          if (!item.items?.length) {
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  isActive={isParentActive}
                  className={navButtonClasses}
                  tooltip={item.title}
                >
                  <Link to={item.url}>
                    <item.icon className="size-5" color={iconColor} />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          }

          // Icon-collapsed mode: keep a compact flyout so nested links stay reachable
          if (isCollapsed) {
            return (
              <SidebarMenuItem key={item.title}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton
                      isActive={isParentActive}
                      className={navButtonClasses}
                      tooltip={item.title}
                    >
                      <item.icon className="size-5" color={iconColor} />
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="right"
                    align="start"
                    className="w-48"
                    sideOffset={4}
                  >
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      {item.title}
                    </DropdownMenuLabel>
                    {item.items.map((subItem) => (
                      <DropdownMenuItem key={subItem.title} asChild>
                        <Link to={subItem.url}>{subItem.title}</Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            );
          }

          return (
            <Collapsible
              key={item.title}
              asChild
              defaultOpen={isParentActive}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    isActive={isParentActive}
                    className={navButtonClasses}
                  >
                    <item.icon className="size-5 shrink-0" color={iconColor} />
                    <span className="truncate">{item.title}</span>
                    <ChevronRight className="size-3 ml-auto opacity-60 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items.map((subItem) => {
                      const isSubActive =
                        pathname === subItem.url ||
                        pathname.startsWith(`${subItem.url}/`);

                      return (
                        <SidebarMenuSubItem key={subItem.title}>
                          <SidebarMenuSubButton asChild isActive={isSubActive}>
                            <Link to={subItem.url}>
                              <span>{subItem.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      );
                    })}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
